/** The four states every data surface has (G4): loading, empty, error, permission-denied. */
import type { ReactNode } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { Button } from './Button';
import { ReportControl } from './ReportControl';
import { findFailure } from '@/lib/failures';

export function LoadingState({ label = 'Loading…', rows = 4 }: { label?: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-3 py-2" data-state="loading">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton h-10 w-full" style={{ opacity: 1 - i * 0.15 }} aria-hidden />
      ))}
    </div>
  );
}

export function EmptyState({ title, body, action, icon }: { title: string; body?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center" data-state="empty">
      {icon && <div className="text-subtle" aria-hidden>{icon}</div>}
      <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
      {body && <p className="max-w-md text-sm text-ink-2">{body}</p>}
      {action}
    </div>
  );
}

/** Error surface with "Report" (§2.11): one click sends what failed and where; the label then reads "Reported". */
export function ErrorState({ title = "Couldn't load data — please try again.", error, retry, context }: {
  title?: string; error?: unknown; retry?: () => void; context?: Record<string, unknown>;
}) {
  const e = error as { message?: string; code?: string; status?: number } | undefined;
  const eventId = typeof context?.eventId === 'string' ? context.eventId : null;
  // when the title is the sentence a failed server call was given, the report carries that call
  const noted = findFailure(title);
  const text = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return (
    <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center" data-state="error">
      <AlertTriangle className="size-6 text-st-danger" aria-hidden />
      <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
      {/* the explanation behind the info icon opens under this row, from its left edge, so it stays on a narrow screen */}
      <div className="relative flex w-full flex-wrap justify-center gap-2">
        {retry && <Button onClick={retry}>Try again</Button>}
        <ReportControl surface="state" look="ghost" className="static" eventId={eventId}
          failure={{ reason: 'load', message: title, code: text(e?.code) ?? noted?.code, details: text(e?.message) ?? noted?.details, apiPath: noted?.apiPath,
            apiStatus: typeof e?.status === 'number' ? e.status : noted?.apiStatus, traceId: noted?.traceId }} />
      </div>
    </div>
  );
}

export function DeniedState({ reason }: { reason: string }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center" data-state="denied">
      <Lock className="size-6 text-subtle" aria-hidden />
      <h3 className="text-[15px] font-semibold text-ink">You don't have access to this</h3>
      <p className="max-w-md text-sm text-ink-2">{reason}</p>
    </div>
  );
}
